
import { 
  type Unsubscriber, 
  type Writable, 
  derived, 
  get, 
  writable } from 'svelte/store';
import type { ManageAPI } from "~/manage-api/types";
import { type POI, POIData } from './poi';
import { createFSM } from './states';
import { type Translator } from "~/service/l10n";
import { type MapL10n, loadTranslator } from './translate';

export type Floor = {
  id: string;
  index: number;
  name: string;
  short_name: string;
}

export type ConnectMapAppConfig = {
  locale: string;
  initial_poi?: string;
  init_in_fullscreen?: boolean;
};

export abstract class ImplLayer {

  protected app: ConnectMapApp<any>;

  constructor (app: ConnectMapApp<any>) {
    this.app = app;
  }

  abstract resize (): void;

  abstract selectPOI(poi: POI): void;

  static getFontFamily (element: HTMLElement) {
    const [family] = window.getComputedStyle(element).fontFamily.split(',');
    return family || 'ui-system';
  }

  static getDocLang (): string | undefined {
    const [lang] = document.documentElement.getAttribute('lang')?.split('-') || [];
    return lang;
  }
}

export type RouteNode = 'src' | 'dst';

export type ConnectMapEvent = {
  'fullscreen': boolean;
  'map_ready': boolean;
  'tap_poi': POI;
}

type ConnectMapEventType = keyof ConnectMapEvent;
export type ConnectMapEventDispatch = <Type extends ConnectMapEventType>(type: Type, param: ConnectMapEvent[Type]) => void;
export type ConnectMapEventHandler = <Type extends ConnectMapEventType>(event: { type: Type; detail: ConnectMapEvent[Type] }) => void;

export abstract class ConnectMapApp<L extends ImplLayer> {

  readonly map_layer_component: ConstructorOfATypedSvelteComponent | null;
  protected _config: ConnectMapAppConfig;
  get config () { return this._config }

  readonly lang: string;

  /** Finite state machine of the app */
  readonly state;
  readonly fullscreen;
  readonly is_desktop;
  readonly is_mobile;
  desktop_breakpoint = 768;

  readonly floors;
  readonly current_floor;

  readonly search_term;

  readonly stores;

  select_for: RouteNode = 'dst';
  readonly src_poi;
  readonly dst_poi;
  readonly poi_selected;

  readonly poi_data;

  private _impl_layer!: L;
  private translator!: Writable<Translator>;
  protected l10n!: MapL10n;

  protected unsubscribers: Unsubscriber[] = [];

  constructor(config: ConnectMapAppConfig) {

    this.map_layer_component = null;

    this._config = config;
    this.lang = config.locale?.split('-')[0] || '';
    if (!this.lang) throw new Error('No "locale" was provided!');
    this.translator = writable((x) => `${x}`)

    this.state = createFSM(this);

    this.fullscreen = derived(this.state, v => v !== 'embedded_view');
    this.is_desktop = writable(false);
    this.is_mobile = derived(this.is_desktop, v => !v);

    this.floors = writable<Floor[]>([]);
    this.current_floor = writable<Floor>();

    this.search_term = writable('');

    this.stores = writable<readonly ManageAPI.Store[]>([])

    this.src_poi = writable<POI | undefined>();
    this.dst_poi = writable<POI | undefined>();
    this.poi_selected = derived([this.src_poi, this.dst_poi], 
      ([src, dst]) => this.select_for == 'src' ? src : dst
    );

    this.poi_data = new POIData();

    this.unsubscribers.push(...[
      this.stores.subscribe(xs => {
        this.poi_data.add(xs, 'store');
      }),
    ]);
  }

  abstract _initializeImplLayer (element: HTMLElement, dispatch: ConnectMapEventDispatch): Promise<L>;
  abstract _onceImplLayerReady (): Promise<any>;

  async initialize (element: HTMLElement, dispatch: ConnectMapEventDispatch): Promise<void> {
    
    // @ts-ignore: TODO remove later
    window['_app_'] = this;
    this.l10n = await loadTranslator(this.lang);
    this.translator.set(this.l10n.tranlator);

    this._impl_layer = await this._initializeImplLayer(element, dispatch);
    await this._onceImplLayerReady();
    dispatch('map_ready', true);
    this.gotoInitialPOI();
  }

  abstract _destroy (): Promise<void>;

  async destroy () {
    this.unsubscribers.forEach(fn => fn());
    return this._destroy()
  }

  gotoInitialPOI () {
    const initial_poi = this.config.initial_poi || new URLSearchParams(location.search).get('poi');
    if (initial_poi) {
      const poi = this.poi_data.getPoiByID(initial_poi);  
      this.selectPOI(poi as POI);
    }
  }

  updateSearch (search_term: string) {
    this.poi_data.filter(search_term);
  }

  updateSelectFor () {
    const src = get(this.src_poi);
    const dst = get(this.dst_poi);
    if (dst && src || dst) { this.select_for = 'src' }
    else { this.select_for = 'dst' }
  }

  selectPOI (poi: POI) {
    if (!poi) return;
    if (this.select_for == 'dst') {
      this.dst_poi.set(poi);
    }
    else {
      this.src_poi.set(poi);
    }
    
    console.log('selectPOI', this.select_for, poi);
    this.impl_layer.selectPOI(poi);
    this.search_term.set('');
  }

  clearRouteSetup() {
    this.src_poi.set(undefined);
    this.dst_poi.set(undefined);
    this.select_for == 'dst';
  }

  abstract computeRoute(): Promise<void>;
  abstract clearRouting(): void; 

  get impl_layer () {
    return this._impl_layer;
  }

  get T () {
    return this.translator;
  }

  findFloor (floor_id: string) {
    return get(this.floors).find(x => x.id == floor_id);
  }

  async setCurrentFloor (floor: Floor): Promise<void> {
    this.current_floor.set(floor);
  }

  setStores (stores: readonly ManageAPI.Store[]) {
    this.stores.set(stores);
  }

  abstract setRouteOption (option: 'shortest' | 'accessible'): void;
  
}
